import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Select, Switch, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import api from '../adapters/api';

// ── Earn Rules ───────────────────────────────────────────────────────────────

const EARN_RULE_TYPES = [
  { value: 'order_amount', label: 'ยอดสั่งซื้อ (บาท → แต้ม)' },
  { value: 'product', label: 'สินค้าเฉพาะ (ต่อหน่วย)' },
  { value: 'category', label: 'หมวดหมู่ (ต่อหน่วย)' },
  { value: 'qty_threshold', label: 'จำนวนขั้นต่ำ (แต้มโบนัส)' },
  { value: 'special_promo', label: 'คูณแต้ม (พิเศษ)' },
];

function EarnRuleValueForm({ ruleType, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  switch (ruleType) {
    case 'order_amount':
      return <TextField size="small" fullWidth type="number" label="แต้มต่อบาท (เช่น 0.01 = 1 แต้มต่อ 100 บาท)" value={value.points_per_baht || ''} onChange={e => set('points_per_baht', Number(e.target.value))} />;
    case 'product':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField size="small" fullWidth label="รหัสสินค้า (คั่นด้วย ,)" value={(value.item_codes || []).join(', ')} onChange={e => set('item_codes', e.target.value.split(',').map(s => s.trim()))} />
        <TextField size="small" fullWidth type="number" label="แต้มต่อหน่วย" value={value.points_per_unit || ''} onChange={e => set('points_per_unit', Number(e.target.value))} />
      </Box>;
    case 'category':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField size="small" fullWidth label="หมวดหมู่ (item_pattern, คั่นด้วย ,)" value={(value.categories || []).join(', ')} onChange={e => set('categories', e.target.value.split(',').map(s => s.trim()))} />
        <TextField size="small" fullWidth type="number" label="แต้มต่อหน่วย" value={value.points_per_unit || ''} onChange={e => set('points_per_unit', Number(e.target.value))} />
      </Box>;
    case 'qty_threshold':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField size="small" fullWidth label="รหัสสินค้า (คั่นด้วย ,)" value={(value.item_codes || []).join(', ')} onChange={e => set('item_codes', e.target.value.split(',').map(s => s.trim()))} />
        <TextField size="small" fullWidth type="number" label="จำนวนขั้นต่ำ" value={value.min_qty || ''} onChange={e => set('min_qty', Number(e.target.value))} />
        <TextField size="small" fullWidth type="number" label="แต้มโบนัส" value={value.bonus_points || ''} onChange={e => set('bonus_points', Number(e.target.value))} />
      </Box>;
    case 'special_promo':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField size="small" fullWidth label="รหัสสินค้า (คั่นด้วย ,)" value={(value.item_codes || []).join(', ')} onChange={e => set('item_codes', e.target.value.split(',').map(s => s.trim()))} />
        <TextField size="small" fullWidth type="number" label="ตัวคูณแต้ม (เช่น 2 = คูณ 2)" value={value.points_multiplier || ''} onChange={e => set('points_multiplier', Number(e.target.value))} />
      </Box>;
    default:
      return null;
  }
}

function EarnRuleDialog({ open, onClose, onSaved, initial }) {
  const empty = { name: '', is_active: true, start_date: '', end_date: '', rule_type: 'order_amount', rule_value: {}, priority: 0 };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const ep = form.id ? '/api/loyalty/earn-rules/update' : '/api/loyalty/earn-rules/create';
      await api.post(ep, { ...form, priority: Number(form.priority) || 0 }, { withCredentials: true });
      onSaved(); onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || '', confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form.id ? 'แก้ไขกฎสะสมแต้ม' : 'สร้างกฎสะสมแต้มใหม่'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField required fullWidth size="small" label="ชื่อกฎ" value={form.name} onChange={e => set('name', e.target.value)} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" type="date" label="วันเริ่มต้น" InputLabelProps={{ shrink: true }} value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
            <TextField fullWidth size="small" type="date" label="วันสิ้นสุด" InputLabelProps={{ shrink: true }} value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
          </Box>
          <TextField fullWidth size="small" type="number" label="ลำดับความสำคัญ (สูง = ก่อน)" value={form.priority} onChange={e => set('priority', e.target.value)} />
          <FormControl size="small" fullWidth>
            <InputLabel>ประเภทกฎ</InputLabel>
            <Select label="ประเภทกฎ" value={form.rule_type} onChange={e => set('rule_type', e.target.value)}>
              {EARN_RULE_TYPES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <EarnRuleValueForm ruleType={form.rule_type} value={form.rule_value || {}} onChange={v => set('rule_value', v)} />
          <FormControlLabel control={<Switch checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />} label="เปิดใช้งาน" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#757575' }}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'บันทึก'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Rewards ──────────────────────────────────────────────────────────────────

function RewardDialog({ open, onClose, onSaved, initial }) {
  const empty = { name: '', description: '', points_required: 0, reward_type: 'gift', item_code: '', item_qty: '', item_unit: '', discount_amount: '', stock: '', is_active: true, start_date: '', end_date: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const ep = form.id ? '/api/loyalty/rewards/update' : '/api/loyalty/rewards/create';
      await api.post(ep, { ...form, points_required: Number(form.points_required), item_qty: form.item_qty ? Number(form.item_qty) : null, discount_amount: form.discount_amount ? Number(form.discount_amount) : null, stock: form.stock ? Number(form.stock) : null }, { withCredentials: true });
      onSaved(); onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || '', confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form.id ? 'แก้ไขของรางวัล' : 'สร้างของรางวัลใหม่'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField required fullWidth size="small" label="ชื่อของรางวัล" value={form.name} onChange={e => set('name', e.target.value)} />
          <TextField fullWidth size="small" label="คำอธิบาย" multiline rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)} />
          <TextField required fullWidth size="small" type="number" label="แต้มที่ต้องใช้" value={form.points_required} onChange={e => set('points_required', e.target.value)} />
          <FormControl size="small" fullWidth>
            <InputLabel>ประเภทรางวัล</InputLabel>
            <Select label="ประเภทรางวัล" value={form.reward_type} onChange={e => set('reward_type', e.target.value)}>
              <MenuItem value="gift">ของแถม (เพิ่มสินค้าในบิล)</MenuItem>
              <MenuItem value="discount">ส่วนลด (บาท)</MenuItem>
            </Select>
          </FormControl>
          {form.reward_type === 'gift' && <>
            <TextField fullWidth size="small" label="รหัสสินค้าแถม" value={form.item_code || ''} onChange={e => set('item_code', e.target.value)} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth size="small" type="number" label="จำนวน" value={form.item_qty || ''} onChange={e => set('item_qty', e.target.value)} />
              <TextField fullWidth size="small" label="หน่วย" value={form.item_unit || ''} onChange={e => set('item_unit', e.target.value)} />
            </Box>
          </>}
          {form.reward_type === 'discount' && <TextField fullWidth size="small" type="number" label="จำนวนส่วนลด (บาท)" value={form.discount_amount || ''} onChange={e => set('discount_amount', e.target.value)} />}
          <TextField fullWidth size="small" type="number" label="จำนวนคงเหลือ (ว่าง = ไม่จำกัด)" value={form.stock || ''} onChange={e => set('stock', e.target.value)} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" type="date" label="วันเริ่มต้น" InputLabelProps={{ shrink: true }} value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
            <TextField fullWidth size="small" type="date" label="วันสิ้นสุด" InputLabelProps={{ shrink: true }} value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
          </Box>
          <FormControlLabel control={<Switch checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />} label="เปิดใช้งาน" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#757575' }}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'บันทึก'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Loyalty() {
  const [tab, setTab] = useState(0);

  // Earn rules state
  const [earnRules, setEarnRules] = useState([]);
  const [earnDialogOpen, setEarnDialogOpen] = useState(false);
  const [earnTarget, setEarnTarget] = useState(null);

  // Rewards state
  const [rewards, setRewards] = useState([]);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [rewardTarget, setRewardTarget] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({ points_to_baht_rate: 0.1, min_points_to_redeem: 100, max_redeem_percent: '', point_expiry_months: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);

  // History state
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [histCustCode, setHistCustCode] = useState('');
  const [histPage, setHistPage] = useState(0);

  const loadEarnRules = useCallback(async () => {
    const { data } = await api.post('/api/loyalty/earn-rules/list', {}, { withCredentials: true });
    setEarnRules(data || []);
  }, []);

  const loadRewards = useCallback(async () => {
    const { data } = await api.post('/api/loyalty/rewards/list', {}, { withCredentials: true });
    setRewards(data || []);
  }, []);

  const loadSettings = useCallback(async () => {
    const { data } = await api.post('/api/loyalty/settings/get', {}, { withCredentials: true });
    setSettings(data || {});
  }, []);

  const loadHistory = useCallback(async () => {
    if (!histCustCode) return;
    const { data } = await api.post('/api/loyalty/history', { cust_code: histCustCode, page: histPage + 1 }, { withCredentials: true });
    setHistoryRows(data.rows || []);
    setHistoryTotal(data.total || 0);
  }, [histCustCode, histPage]);

  useEffect(() => { if (tab === 0) loadEarnRules(); }, [tab, loadEarnRules]);
  useEffect(() => { if (tab === 1) loadRewards(); }, [tab, loadRewards]);
  useEffect(() => { if (tab === 2) loadSettings(); }, [tab, loadSettings]);
  useEffect(() => { if (tab === 3) loadHistory(); }, [tab, loadHistory]);

  const handleDeleteEarnRule = async (id) => {
    const r = await Swal.fire({ icon: 'warning', title: 'ปิดใช้งานกฎนี้?', showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#757575', confirmButtonText: 'ปิดใช้งาน', cancelButtonText: 'ยกเลิก' });
    if (!r.isConfirmed) return;
    await api.post('/api/loyalty/earn-rules/delete', { id }, { withCredentials: true });
    loadEarnRules();
  };

  const handleDeleteReward = async (id) => {
    const r = await Swal.fire({ icon: 'warning', title: 'ปิดใช้งานของรางวัลนี้?', showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#757575', confirmButtonText: 'ปิดใช้งาน', cancelButtonText: 'ยกเลิก' });
    if (!r.isConfirmed) return;
    await api.post('/api/loyalty/rewards/delete', { id }, { withCredentials: true });
    loadRewards();
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await api.post('/api/loyalty/settings/update', {
        ...settings,
        points_to_baht_rate: Number(settings.points_to_baht_rate),
        min_points_to_redeem: Number(settings.min_points_to_redeem),
        max_redeem_percent: settings.max_redeem_percent ? Number(settings.max_redeem_percent) : null,
        point_expiry_months: settings.point_expiry_months ? Number(settings.point_expiry_months) : null,
      }, { withCredentials: true });
      Swal.fire({ icon: 'success', title: 'บันทึกสำเร็จ', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || '', confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
    } finally { setSettingsSaving(false); }
  };

  const earnColumns = [
    { field: 'name', headerName: 'ชื่อกฎ', flex: 2 },
    { field: 'rule_type', headerName: 'ประเภท', width: 180, renderCell: ({ value }) => EARN_RULE_TYPES.find(r => r.value === value)?.label || value },
    { field: 'priority', headerName: 'ลำดับ', width: 80, type: 'number' },
    { field: 'is_active', headerName: 'สถานะ', width: 90, renderCell: ({ value }) => <Chip label={value ? 'เปิด' : 'ปิด'} size="small" color={value ? 'success' : 'default'} /> },
    { field: 'actions', headerName: '', width: 90, sortable: false, renderCell: ({ row }) => <Box><IconButton size="small" onClick={() => { setEarnTarget(row); setEarnDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton><IconButton size="small" color="error" onClick={() => handleDeleteEarnRule(row.id)}><DeleteIcon fontSize="small" /></IconButton></Box> },
  ];

  const rewardColumns = [
    { field: 'name', headerName: 'ชื่อของรางวัล', flex: 2 },
    { field: 'reward_type', headerName: 'ประเภท', width: 120, renderCell: ({ value }) => value === 'gift' ? 'ของแถม' : 'ส่วนลด' },
    { field: 'points_required', headerName: 'แต้มที่ต้องใช้', width: 120, type: 'number' },
    { field: 'stock', headerName: 'คงเหลือ', width: 100, renderCell: ({ value }) => value == null ? 'ไม่จำกัด' : value },
    { field: 'is_active', headerName: 'สถานะ', width: 90, renderCell: ({ value }) => <Chip label={value ? 'เปิด' : 'ปิด'} size="small" color={value ? 'success' : 'default'} /> },
    { field: 'actions', headerName: '', width: 90, sortable: false, renderCell: ({ row }) => <Box><IconButton size="small" onClick={() => { setRewardTarget(row); setRewardDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton><IconButton size="small" color="error" onClick={() => handleDeleteReward(row.id)}><DeleteIcon fontSize="small" /></IconButton></Box> },
  ];

  const historyColumns = [
    { field: 'created_at', headerName: 'วันที่', width: 160, valueFormatter: (value) => value ? new Date(value).toLocaleString('th-TH') : '-' },
    { field: 'transaction_type', headerName: 'ประเภท', width: 130 },
    { field: 'points', headerName: 'แต้ม', width: 100, type: 'number' },
    { field: 'doc_no', headerName: 'เลขที่เอกสาร', width: 140 },
    { field: 'expires_at', headerName: 'หมดอายุ', width: 110, valueFormatter: (value) => value ? String(value).slice(0, 10) : '-' },
    { field: 'note', headerName: 'หมายเหตุ', flex: 1 },
  ];

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" fontWeight={600}>ระบบแต้มสะสม</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="กฎสะสมแต้ม" />
        <Tab label="ของรางวัล" />
        <Tab label="ตั้งค่า" />
        <Tab label="ประวัติแต้ม" />
      </Tabs>

      {/* Tab 0: Earn Rules */}
      {tab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEarnTarget(null); setEarnDialogOpen(true); }}>สร้างกฎใหม่</Button>
          </Box>
          <DataGrid rows={earnRules} columns={earnColumns} disableRowSelectionOnClick sx={{ flex: 1 }} />
          <EarnRuleDialog open={earnDialogOpen} onClose={() => setEarnDialogOpen(false)} onSaved={loadEarnRules} initial={earnTarget} />
        </Box>
      )}

      {/* Tab 1: Rewards */}
      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setRewardTarget(null); setRewardDialogOpen(true); }}>สร้างของรางวัลใหม่</Button>
          </Box>
          <DataGrid rows={rewards} columns={rewardColumns} disableRowSelectionOnClick sx={{ flex: 1 }} />
          <RewardDialog open={rewardDialogOpen} onClose={() => setRewardDialogOpen(false)} onSaved={loadRewards} initial={rewardTarget} />
        </Box>
      )}

      {/* Tab 2: Settings */}
      {tab === 2 && (
        <Box sx={{ maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField size="small" type="number" label="อัตราแต้มต่อบาท (เช่น 0.1 = 100 แต้ม = 10 บาท)" value={settings.points_to_baht_rate} onChange={e => setSettings(s => ({ ...s, points_to_baht_rate: e.target.value }))} />
          <TextField size="small" type="number" label="แต้มขั้นต่ำในการแลก" value={settings.min_points_to_redeem} onChange={e => setSettings(s => ({ ...s, min_points_to_redeem: e.target.value }))} />
          <TextField size="small" type="number" label="% สูงสุดของยอดบิลที่แลกได้ (ว่าง = ไม่จำกัด)" value={settings.max_redeem_percent || ''} onChange={e => setSettings(s => ({ ...s, max_redeem_percent: e.target.value }))} />
          <TextField size="small" type="number" label="อายุแต้ม (เดือน, ว่าง = ไม่หมดอายุ)" value={settings.point_expiry_months || ''} onChange={e => setSettings(s => ({ ...s, point_expiry_months: e.target.value }))} />
          <Button variant="contained" onClick={handleSaveSettings} disabled={settingsSaving} sx={{ alignSelf: 'flex-start' }}>
            {settingsSaving ? <CircularProgress size={18} /> : 'บันทึกการตั้งค่า'}
          </Button>
        </Box>
      )}

      {/* Tab 3: History */}
      {tab === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField size="small" label="รหัสลูกค้า" value={histCustCode} onChange={e => setHistCustCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadHistory()} sx={{ width: 200 }} />
            <Button variant="outlined" onClick={loadHistory}>ค้นหา</Button>
          </Box>
          <DataGrid
            rows={historyRows}
            columns={historyColumns}
            rowCount={historyTotal}
            paginationMode="server"
            paginationModel={{ page: histPage, pageSize: 50 }}
            onPaginationModelChange={({ page: p }) => setHistPage(p)}
            disableRowSelectionOnClick
            sx={{ flex: 1 }}
          />
        </Box>
      )}
    </Box>
  );
}
